/**
 * Thinking / Working chrome: a visible assistant reply covers the last visitor
 * turn even when merge/timestamp order leaves YOU last.
 */

import { normalizeGreetingCompareText } from '../core/prompts/greetings.js'

export interface ThinkingLatchRow {
  id?: string | undefined
  role: string
  text?: string | undefined
  attachment?: { type?: string } | undefined
  isFinal?: boolean | undefined
  status?: string | undefined
}

export function isFirstContactGreetingId(id: string | undefined | null): boolean {
  return typeof id === 'string' && id.startsWith('greeting-')
}

function rowText(row: ThinkingLatchRow): string {
  return row.text?.trim() ?? ''
}

function isVisitorRow(row: ThinkingLatchRow): boolean {
  return row.role === 'user'
}

function isAssistantRow(row: ThinkingLatchRow): boolean {
  return row.role === 'model' || row.role === 'assistant'
}

export function hasVisibleAssistantText(row: ThinkingLatchRow): boolean {
  if (!isAssistantRow(row)) return false
  if (row.attachment?.type === 'intelligence_briefing') return false
  return Boolean(rowText(row))
}

function greetingTexts(rows: readonly ThinkingLatchRow[]): Set<string> {
  const texts = new Set<string>()
  for (const row of rows) {
    if (!isFirstContactGreetingId(row.id) || !hasVisibleAssistantText(row)) continue
    texts.add(normalizeGreetingCompareText(rowText(row)))
  }
  return texts
}

function lastVisitorIndex(rows: readonly ThinkingLatchRow[]): number {
  for (let index = rows.length - 1; index >= 0; index -= 1) {
    const row = rows[index]
    if (row && isVisitorRow(row)) return index
  }
  return -1
}

/**
 * Trailing empty assistant shells (gate/REST loading rows) are not replies.
 * Last meaningful row is the last visitor text or visible assistant text.
 */
export function lastMeaningfulTurnIsUnansweredVisitor(
  rows: readonly ThinkingLatchRow[]
): boolean {
  for (let index = rows.length - 1; index >= 0; index -= 1) {
    const row = rows[index]
    if (!row) continue
    if (isVisitorRow(row) && rowText(row)) {
      return !lastVisitorTurnHasCompletedReply(rows)
    }
    if (hasVisibleAssistantText(row)) return false
  }
  return false
}

/**
 * True when the last visitor turn already has a visible assistant reply.
 * Greeting-* (and a [SAY] echo of that same text) does not cover a later YOU.
 */
export function lastVisitorTurnHasCompletedReply(
  rows: readonly ThinkingLatchRow[]
): boolean {
  const lastUserIndex = lastVisitorIndex(rows)
  if (lastUserIndex < 0) return rows.some(hasVisibleAssistantText)

  for (let index = lastUserIndex + 1; index < rows.length; index += 1) {
    const row = rows[index]
    if (row && hasVisibleAssistantText(row)) return true
  }

  const greetings = greetingTexts(rows)
  for (let index = 0; index < lastUserIndex; index += 1) {
    const row = rows[index]
    if (!row || !hasVisibleAssistantText(row)) continue
    if (isFirstContactGreetingId(row.id)) continue
    if (greetings.has(normalizeGreetingCompareText(rowText(row)))) continue
    return true
  }

  return false
}

/** Live drop must not keep Working/Stop unless REST/Live generation is still in flight. */
export function shouldHoldPendingReplyChrome(input: {
  pendingReply: boolean
  liveConnected?: boolean
  liveConnecting?: boolean
  isThinking?: boolean
  isGenerating?: boolean
  isStreaming?: boolean
}): boolean {
  if (!input.pendingReply) return false
  if (input.isThinking || input.isGenerating || input.isStreaming) return true
  return Boolean(input.liveConnected || input.liveConnecting)
}

/** Mic-open corrections must leave the composer. Pending-reply chrome stays for text-only turns. */
export function isComposerGenerationLocked(input: {
  voiceConnected: boolean
  turnBusy: boolean
}): boolean {
  if (input.voiceConnected) return false
  return input.turnBusy
}
